<template>
  <div class="last-minute-lessons">
    <h3>Ripetizioni Last Minute</h3>

    <!-- Show recommendations if subject selected and confirmation shown -->
    <div v-if="showConfirmation && selectedSubject && showRecommendations">
      <div class="alert alert-success mb-4">
        <i class="fas fa-check-circle me-2"></i>
        <strong>Richiesta urgente attiva per {{ selectedSubject.nome }}!</strong>
      </div>

      <TutorRecommendations
        :student-id="currentUserId"
        :subject-name="selectedSubject.nome"
        :show-back-button="false"
        :initial-count="5"
        @view-tutor-details="handleViewTutorDetails"
        @view-profile="handleViewProfile"
        @book-slot="handleBookSlot"
        @show-all-tutors="handleShowAllTutors"
      />

      <div class="text-center mt-4">
        <button class="btn btn-outline-secondary" @click="cancelEmergencyRequest">
          <i class="fas fa-times me-1"></i>
          Annulla richiesta urgente
        </button>
      </div>
    </div>

    <!-- Subject selection (only show if no confirmation) -->
    <div v-else>
      <div class="subject-selection mb-4">
        <h5>Seleziona la materia per cui hai bisogno di aiuto urgente</h5>
        <SubjectList
            :items="subjects"
            @select="handleSubjectSelect"
        />
      </div>

      <!-- Existing confirmation message (fallback) -->
      <div v-if="showConfirmation && !showRecommendations" class="confirmation mt-4">
        <div class="alert alert-success d-flex justify-content-between align-items-center">
          <div>
            Hai richiesto una ripetizione last-minute per {{ selectedSubject.nome }}! Chiudi la dashboard e attendi che qualcuno si colleghi.
          </div>
          <button class="btn btn-primary btn-sm" @click="findTutorNow">
            <i class="fas fa-search me-1"></i>
            Trova Tutor Ora
          </button>
        </div>
      </div>
    </div>

    <!-- Modals for tutor details and profile -->
    <div v-if="showTutorDetails" class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5);">
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Disponibilità - {{ selectedTutorDetails.tutorName }}</h5>
            <button type="button" class="btn-close" @click="closeTutorDetails"></button>
          </div>
          <div class="modal-body">
            <DisponibilitaMateria
              v-if="selectedTutorSubject"
              :materia="selectedTutorSubject"
              :currentUser="{ id: currentUserId }"
              :specific-tutor-id="selectedTutorDetails.tutorId"
              :specific-tutor-name="selectedTutorDetails.tutorName"
            />
          </div>
        </div>
      </div>
    </div>

    <div v-if="showProfileModal" class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5);">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Profilo Tutor</h5>
            <button type="button" class="btn-close" @click="closeProfileModal"></button>
          </div>
          <div class="modal-body">
            <UserProfile
              :userId="selectedProfileId"
              :is-own-profile="false"
              :student="false"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import axios from 'axios';
import config from '@/utils/config';
import SubjectList from './SubjectList.vue';
import TutorRecommendations from './TutorRecommendations.vue';
import DisponibilitaMateria from './DisponibilitaMateria.vue';
import UserProfile from './UserProfile.vue';

export default {
  name: 'LastMinuteLessons',
  components: {
    SubjectList,
    TutorRecommendations,
    DisponibilitaMateria,
    UserProfile
  },
  props: {
    currentUserId: {
      type: Number,
      required: true
    }
  },
  setup(props) {
    const subjects = ref([]);
    const selectedSubject = ref(null);
    const showConfirmation = ref(false);
    const showRecommendations = ref(false);
    const showTutorDetails = ref(false);
    const selectedTutorDetails = ref(null);
    const selectedTutorSubject = ref(null);
    const showProfileModal = ref(false);
    const selectedProfileId = ref(null);
    let checkTimer = null;

    const loadSubjects = async () => {
      try {
        const response = await axios.get(`${config.baseUrl}/materia`);
        subjects.value = response.data;
      } catch (error) {
        console.error('Error loading subjects:', error);
      }
    };

    const handleSubjectSelect = async (subject) => {
      selectedSubject.value = subject;
      showConfirmation.value = true;

      try {
        await axios.post(
            `${config.baseUrl}/user/${props.currentUserId}/last-minute`,
            subject.nome,
            {
              headers: {
                'Content-Type': 'text/plain'
              }
            }
        );
        // Automatically show recommendations after creating emergency request
        showRecommendations.value = true;
      } catch (error) {
        console.error('Error saving last-minute request:', error);
        showConfirmation.value = false;
      }
    };

    const findTutorNow = () => {
      showRecommendations.value = true;
    };

    const cancelEmergencyRequest = async () => {
      try {
        await axios.delete(`${config.baseUrl}/user/${props.currentUserId}/last-minute`);
        showConfirmation.value = false;
        showRecommendations.value = false;
        selectedSubject.value = null;
      } catch (error) {
        console.error('Error canceling last-minute request:', error);
      }
    };

    const handleViewTutorDetails = (tutorData) => {
      selectedTutorDetails.value = tutorData;
      // Find the subject object
      selectedTutorSubject.value = subjects.value.find(s => s.nome === tutorData.subject);
      showTutorDetails.value = true;
    };

    const closeTutorDetails = () => {
      showTutorDetails.value = false;
      selectedTutorDetails.value = null;
      selectedTutorSubject.value = null;
    };

    const handleViewProfile = (tutorId) => {
      selectedProfileId.value = tutorId;
      showProfileModal.value = true;
    };

    const closeProfileModal = () => {
      showProfileModal.value = false;
      selectedProfileId.value = null;
    };

    const handleBookSlot = ({ tutor }) => {
      // Navigate to tutor details for booking
      handleViewTutorDetails({
        tutorId: tutor.tutorId,
        tutorName: tutor.tutorName,
        subject: selectedSubject.value.nome
      });
    };

    const handleShowAllTutors = () => {
      // Could navigate to full tutor list if needed
      console.log('Show all tutors');
    };

    const checkLastMinuteRequest = async () => {
      try {
        const response = await axios.get(
            `${config.baseUrl}/user/${props.currentUserId}/last-minute`
        );
        if (response.data) {
          const subject = subjects.value.find(s => s.nome === response.data);
          if (subject) {
            selectedSubject.value = subject;
            showConfirmation.value = true;
            // If there was already a request, show recommendations
            if (!showRecommendations.value) {
              showRecommendations.value = false; // User can click "Find Tutor Now"
            }
          }
        } else {
          showConfirmation.value = false;
          showRecommendations.value = false;
          selectedSubject.value = null;
        }
      } catch (error) {
        console.error('Error checking last-minute request:', error);
        showConfirmation.value = false;
        showRecommendations.value = false;
      }
    };

    onMounted(() => {
      loadSubjects();
      checkLastMinuteRequest();

      // Check every minute if the request is still valid
      checkTimer = setInterval(checkLastMinuteRequest, 60000);
    });

    onBeforeUnmount(() => {
      if (checkTimer) {
        clearInterval(checkTimer);
      }
    });

    return {
      subjects,
      selectedSubject,
      showConfirmation,
      showRecommendations,
      showTutorDetails,
      selectedTutorDetails,
      selectedTutorSubject,
      showProfileModal,
      selectedProfileId,
      handleSubjectSelect,
      findTutorNow,
      cancelEmergencyRequest,
      handleViewTutorDetails,
      closeTutorDetails,
      handleViewProfile,
      closeProfileModal,
      handleBookSlot,
      handleShowAllTutors
    };
  }
};
</script>

<style scoped>
.last-minute-lessons {
  padding: 20px;
}
</style>